import React, { useCallback, useEffect, useState } from 'react';
import { Heading } from '../../components/Heading';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/Card';
import Loading from '../../components/Loading';
import ServerHealthService, { ServerHealth, SqlAccountTestResult, SqlAccountWarehouse } from '../../services/serverHealthService';
import showErrorToast from '../../components/Toasts/ShowErrorToast';
import { ArrowPathIcon, SignalIcon } from '@heroicons/react/16/solid';

function formatUptime(totalSeconds: number): string {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
}

function StatusBadge({ ok, okLabel = 'Healthy', failLabel = 'Unhealthy' }: { ok: boolean; okLabel?: string; failLabel?: string }) {
    return <Badge color={ok ? 'green' : 'red'}>{ok ? okLabel : failLabel}</Badge>;
}

function StatItem({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="mt-1 text-sm font-medium text-card-foreground">{value}</div>
        </div>
    );
}

type TestState = SqlAccountTestResult & { testedAt: string };

const ServerHealthPage: React.FC = () => {
    const [health, setHealth] = useState<ServerHealth | null>(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [healthError, setHealthError] = useState<string | null>(null);

    const [warehouses, setWarehouses] = useState<SqlAccountWarehouse[]>([]);
    const [warehousesLoading, setWarehousesLoading] = useState(false);
    const [testResults, setTestResults] = useState<Record<number, TestState>>({});
    const [testingId, setTestingId] = useState<number | null>(null);

    const fetchHealth = useCallback(async () => {
        setHealthLoading(true);
        setHealthError(null);
        try {
            const res = await ServerHealthService.getHealth();
            setHealth(res);
        } catch (err: any) {
            setHealthError(err?.response?.data?.message || 'Failed to check server health');
        } finally {
            setHealthLoading(false);
        }
    }, []);

    const fetchWarehouses = useCallback(async () => {
        setWarehousesLoading(true);
        try {
            const res = await ServerHealthService.getSqlAccountWarehouses();
            setWarehouses(res.warehouses);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to load SQL Account warehouses');
        } finally {
            setWarehousesLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Server Health';
        fetchHealth();
        fetchWarehouses();
    }, [fetchHealth, fetchWarehouses]);

    const handleTestWarehouse = async (warehouseId: number) => {
        setTestingId(warehouseId);
        try {
            const res = await ServerHealthService.testSqlAccount(warehouseId);
            setTestResults((prev) => ({ ...prev, [warehouseId]: { ...res, testedAt: new Date().toISOString() } }));
            if (!res.ok) {
                showErrorToast(`${res.label}: connection test failed`);
            }
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to run the connection test');
        } finally {
            setTestingId(null);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Heading>Server Health</Heading>
                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                        Check this app server's own health, and test connectivity to each warehouse's SQL Account install.
                    </p>
                </div>
            </div>

            <Card className="mt-6">
                <CardHeader>
                    <div>
                        <CardTitle>This Server</CardTitle>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            {health ? `Last checked ${new Date(health.timestamp).toLocaleString()}` : 'Not checked yet'}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {health && <StatusBadge ok={health.ok} />}
                        <Button outline onClick={fetchHealth} disabled={healthLoading}>
                            {healthLoading ? <Loading /> : <ArrowPathIcon />}
                            Refresh
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {healthLoading && !health && (
                        <div className="flex justify-center py-10"><Loading /></div>
                    )}
                    {healthError && !health && (
                        <p className="py-6 text-center text-red-600 dark:text-red-400">{healthError}</p>
                    )}
                    {health && (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
                            <StatItem label="Uptime" value={formatUptime(health.uptimeSeconds)} />
                            <StatItem
                                label="Database"
                                value={
                                    <span className="flex items-center gap-1.5">
                                        <StatusBadge ok={health.database.ok} okLabel="Connected" failLabel="Down" />
                                        <span className="text-xs text-muted-foreground">{health.database.latencyMs}ms</span>
                                    </span>
                                }
                            />
                            <StatItem label="Node Version" value={health.nodeVersion} />
                            <StatItem label="Platform" value={health.platform} />
                            <StatItem
                                label="Memory"
                                value={`${health.memory.rssMb} MB used · ${health.memory.freeMb} / ${health.memory.totalMb} MB free`}
                            />
                        </div>
                    )}
                    {health && !health.database.ok && health.database.error && (
                        <p className="mt-4 text-xs text-red-600 dark:text-red-400">Database error: {health.database.error}</p>
                    )}
                </CardBody>
            </Card>

            <Card className="mt-6">
                <CardHeader>
                    <div>
                        <CardTitle>SQL Account Connections</CardTitle>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Each warehouse runs its own on-premise SQL Account install - test each one independently.
                        </p>
                    </div>
                </CardHeader>
                <CardBody>
                    {warehousesLoading && (
                        <div className="flex justify-center py-10"><Loading /></div>
                    )}
                    {!warehousesLoading && warehouses.length === 0 && (
                        <p className="py-6 text-center">No SQL Account warehouses configured.</p>
                    )}
                    {!warehousesLoading && warehouses.length > 0 && (
                        <ul className="divide-y divide-zinc-950/5 dark:divide-white/5">
                            {warehouses.map((w) => {
                                const result = testResults[w.warehouseId];
                                const testing = testingId === w.warehouseId;
                                return (
                                    <li key={w.warehouseId} className="flex flex-wrap items-center justify-between gap-3 py-4">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary">
                                                <SignalIcon className="size-4 text-muted-foreground" />
                                            </span>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-card-foreground">{w.label}</div>
                                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                                    {result ? (
                                                        <>
                                                            <StatusBadge ok={result.ok} okLabel="Reachable" failLabel="Unreachable" />
                                                            {result.ok && <span>{result.latencyMs}ms</span>}
                                                            <span>&middot; tested {new Date(result.testedAt).toLocaleTimeString()}</span>
                                                        </>
                                                    ) : (
                                                        <span>Not tested yet</span>
                                                    )}
                                                </div>
                                                {result && !result.ok && result.error && (
                                                    <div className="mt-1 break-words text-xs text-red-600 dark:text-red-400">{result.error}</div>
                                                )}
                                            </div>
                                        </div>
                                        <Button outline onClick={() => handleTestWarehouse(w.warehouseId)} disabled={testing}>
                                            {testing ? <Loading /> : <SignalIcon />}
                                            Test Connection
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default ServerHealthPage;
